import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './PaymentResult.css';

function PaymentFail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const merchantOid = searchParams.get('oid');
  // PayTR'den gelen fail_message (redirect üzerinden query param olarak taşınıyor)
  const urlFailMessage = searchParams.get('msg');
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  // Dil algılama
  useEffect(() => {
    const savedLang = localStorage.getItem('i18nextLng') || localStorage.getItem('language');
    if (savedLang && ['tr', 'en'].includes(savedLang)) {
      i18n.changeLanguage(savedLang);
    } else {
      const browserLang = navigator.language?.startsWith('tr') ? 'tr' : 'en';
      i18n.changeLanguage(browserLang);
    }
  }, [i18n]);

  const fetchPaymentStatus = useCallback(async (attempt = 0) => {
    if (!merchantOid) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`/payment/status/${merchantOid}`);
      setPayment(response.data.payment);
      setLoading(false);
    } catch (err) {
      console.error('Payment status error:', err);

      if (err.response?.status === 404 && attempt < MAX_RETRIES) {
        setRetryCount(attempt + 1);
        setTimeout(() => fetchPaymentStatus(attempt + 1), RETRY_DELAY);
        return;
      }

      // Kayıt bulunamasa bile fail sayfasını göster
      setLoading(false);
    }
  }, [merchantOid]);

  useEffect(() => {
    fetchPaymentStatus(0);
  }, [fetchPaymentStatus]);

  // Hata mesajını belirle
  // PayTR: fail_message "müşteriye doğrudan gösterilebilir şekilde formatlanmış bir mesajdır"
  const getErrorMessage = () => {
    // 1. URL'den gelen msg param (PayTR redirect'ten, kullanıcıya gösterilebilir format)
    if (urlFailMessage) {
      return urlFailMessage;
    }
    // 2. Redirect'ten DB'ye kaydedilen fail_message
    if (payment?.fail_message) {
      return payment.fail_message;
    }
    // 3. Callback'ten gelen failed_reason_msg
    if (payment?.failed_reason_msg) {
      return payment.failed_reason_msg;
    }
    // 4. Genel mesaj
    return i18n.language === 'tr'
      ? 'Ödemeniz tamamlanamadı. Lütfen tekrar deneyin.'
      : 'Your payment could not be completed. Please try again.';
  };

  if (loading) {
    return (
      <div className="payment-result-page">
        <div className="payment-result-container">
          <div className="payment-result-card">
            <div className="spinner"></div>
            <p>{t('payment.loading', 'Loading...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-page">
      <div className="payment-result-container">
        <div className="payment-result-card fail">
          <div className="icon-circle error">
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="38" fill="none" stroke="#ef4444" strokeWidth="4" />
              <path d="M30 30 L50 50 M50 30 L30 50" stroke="#ef4444" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>

          <h1>{i18n.language === 'tr' ? 'Ödeme Başarısız' : 'Payment Failed'}</h1>
          <p className="error-message">{getErrorMessage()}</p>

          {payment && (
            <div className="payment-details">
              {payment.domain_url && (
                <div className="detail-row">
                  <span className="detail-label">{t('domain.url', 'Domain')}:</span>
                  <span className="detail-value">{payment.domain_url}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">{i18n.language === 'tr' ? 'Durum' : 'Status'}:</span>
                <span className="detail-value">
                  <span className="badge danger">{i18n.language === 'tr' ? 'Başarısız' : 'Failed'}</span>
                </span>
              </div>
            </div>
          )}

          <div className="action-buttons">
            <button className="btn-primary" onClick={() => navigate('/dashboard')}>
              {i18n.language === 'tr' ? 'Tekrar Dene' : 'Try Again'}
            </button>
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              {i18n.language === 'tr' ? 'Dashboard\'a Dön' : 'Back to Dashboard'}
            </button>
          </div>

          <div className="info-box warning">
            <p>
              <strong>💡 {i18n.language === 'tr' ? 'Yardıma mı ihtiyacınız var?' : 'Need help?'}</strong><br />
              {i18n.language === 'tr'
                ? 'Sorun devam ederse lütfen bankanızla iletişime geçin veya farklı bir kart deneyin.'
                : 'If the problem persists, please contact your bank or try a different card.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentFail;
